import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { X, FileDown, Eye, FileText, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = [
  { id: "classic", label: "Classic", accent: "#1F5FA8", light: "#EAF1F9", font: "Calibri, 'Segoe UI', sans-serif" },
  { id: "modern",  label: "Teal",    accent: "#0F766E", light: "#E6F4F1", font: "Verdana, sans-serif" },
  { id: "minimal", label: "Mono",    accent: "#111827", light: "#F3F4F6", font: "Georgia, serif" },
  { id: "royal",   label: "Maroon",  accent: "#8B1E3F", light: "#F9EBF0", font: "Cambria, Georgia, serif" },
  { id: "emerald", label: "Green",   accent: "#1B7A43", light: "#E7F4EC", font: "'Trebuchet MS', sans-serif" },
  { id: "gold",    label: "Gold",    accent: "#B45309", light: "#FBF1E2", font: "Arial, sans-serif" },
];
const FONT_SIZES = [
  { id: "small",  label: "Small",  base: 11.5 },
  { id: "normal", label: "Normal", base: 13 },
  { id: "large",  label: "Large",  base: 15 },
];

const money = (v) => `Rs. ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const QuotePreviewModal = ({ quote, onClose }) => {
  const [lh, setLh] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState(quote.company_profile_id || "");
  const [template, setTemplate] = useState(quote.template || "classic");
  const [fontSize, setFontSize] = useState(quote.font_size || "normal");

  useEffect(() => {
    api.get("/settings/letterhead").then((r) => setLh(r.data)).catch(() => setLh({}));
    api.get("/settings/company-profiles").then((r) => setProfiles(r.data)).catch(() => setProfiles([]));
  }, []);

  const activeLh = profileId ? (profiles.find((p) => p.id === profileId) || lh) : lh;

  const tpl = TEMPLATES.find((t) => t.id === template) || TEMPLATES[0];
  const fsz = FONT_SIZES.find((f) => f.id === fontSize) || FONT_SIZES[1];
  const base = fsz.base;

  const totals = (quote.items || []).reduce((acc, i) => {
    const line = (Number(i.quantity) || 0) * (Number(i.unit_price) || 0);
    const disc = line * (Number(i.discount_percent) || 0) / 100;
    const tax = (line - disc) * (Number(i.tax_percent) || 0) / 100;
    acc.sub += line; acc.disc += disc; acc.tax += tax; return acc;
  }, { sub: 0, disc: 0, tax: 0 });
  const grand = totals.sub - totals.disc + totals.tax;

  const download = async (fmt) => {
    try {
      await api.patch(`/quotations/${quote.id}`, { template, font_size: fontSize, company_profile_id: profileId || null });
    } catch { /* non-fatal */ }
    window.open(`${BACKEND_URL}/api/quotations/${quote.id}/${fmt}?template=${template}&font_size=${fontSize}`, "_blank");
  };

  const terms = quote.terms || "1. Prices are exclusive of any charges not mentioned above.\n2. Payment: 100% against delivery unless agreed otherwise.\n3. Delivery: 1-2 weeks from confirmed PO.\n4. Quotation validity as mentioned above.";
  const created = quote.created_at ? new Date(quote.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="flex h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-md border border-border/60 bg-card" onClick={(e) => e.stopPropagation()}>
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-5 py-3">
          <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
            <Eye className="h-4 w-4 text-primary" /> Preview — {quote.quote_no}
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            {profiles.length > 0 && (
              <select data-testid="preview-company-select" value={profileId} onChange={(e) => setProfileId(e.target.value)}
                className="rounded-md border border-border/70 bg-background px-2 py-1.5 text-xs font-medium text-foreground"
                title="Company letterhead for this quote">
                <option value="">Default letterhead</option>
                {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            )}
            <div className="flex items-center gap-1 rounded-md border border-border/70 p-1">
              {TEMPLATES.map((t) => (
                <button key={t.id} data-testid={`preview-template-${t.id}`} onClick={() => setTemplate(t.id)}
                  className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium ${template === t.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>
                  <span className="h-3 w-3 rounded-sm" style={{ background: t.accent }} /> {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 rounded-md border border-border/70 p-1">
              {FONT_SIZES.map((f) => (
                <button key={f.id} data-testid={`preview-font-${f.id}`} onClick={() => setFontSize(f.id)}
                  className={`rounded px-2.5 py-1 text-xs font-medium ${fontSize === f.id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="preview-download-menu"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
                  <FileDown className="h-3.5 w-3.5" /> Download <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="preview-download-docx" onClick={() => download("docx")}>
                  <FileDown className="mr-2 h-3.5 w-3.5" /> Word (.docx)
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="preview-download-pdf" onClick={() => download("pdf")}>
                  <FileText className="mr-2 h-3.5 w-3.5" /> PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button data-testid="preview-close" onClick={onClose} className="rounded-md border border-border/70 p-2"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {/* Paper preview */}
        <div className="flex-1 overflow-y-auto bg-neutral-300/60 p-6 dark:bg-neutral-800/60">
          <div data-testid="quote-preview-paper" className="mx-auto max-w-[820px] bg-white p-10 text-neutral-900 shadow-xl"
            style={{ fontFamily: tpl.font, fontSize: `${base}px` }}>
            {/* Letterhead */}
            <div className="flex items-start justify-between gap-4 pb-3" style={{ borderBottom: `3px solid ${tpl.accent}` }}>
              <div>
                {activeLh?.logo_data ? (
                  <img src={activeLh.logo_data} alt="logo" style={{ height: 54 }} />
                ) : (
                  <p className="font-bold" style={{ color: tpl.accent, fontSize: base * 1.5 }}>{activeLh?.company_name || "Your Company"}</p>
                )}
              </div>
              <div className="text-right">
                {activeLh?.company_name && <p className="font-bold" style={{ color: tpl.accent, fontSize: base * 1.25 }}>{activeLh.company_name}</p>}
                {activeLh?.address && <p style={{ color: "#6b7280", fontSize: base * 0.82 }}>{activeLh.address}</p>}
                {(activeLh?.phone || activeLh?.email) && <p style={{ color: "#6b7280", fontSize: base * 0.82 }}>{[activeLh.phone, activeLh.email].filter(Boolean).join(" · ")}</p>}
              </div>
            </div>

            <p className="py-3 text-center font-bold tracking-wide" style={{ color: tpl.accent, fontSize: base * 1.7 }}>QUOTATION</p>

            {/* To + meta */}
            <div className="flex justify-between gap-6">
              <div className="rounded px-4 py-3" style={{ background: tpl.light, fontSize: base * 0.9, minWidth: "45%" }}>
                <p>To,</p>
                <p className="font-bold">{quote.customer_name || "—"}</p>
              </div>
              <div className="text-right" style={{ fontSize: base * 0.9 }}>
                <p><span style={{ color: "#6b7280" }}>Quotation No:</span> <b>{quote.quote_no || "—"}</b></p>
                <p><span style={{ color: "#6b7280" }}>Date:</span> <b>{created}</b></p>
                <p><span style={{ color: "#6b7280" }}>Valid Until:</span> <b>{quote.valid_until || "—"}</b></p>
                <p><span style={{ color: "#6b7280" }}>Status:</span> <b className="capitalize">{quote.status || "draft"}</b></p>
              </div>
            </div>

            {quote.subject && <p className="mt-4 font-bold" style={{ fontSize: base * 1.02 }}>Subject: {quote.subject}</p>}
            <p className="mt-2" style={{ fontSize: base * 0.92 }}>Dear Sir/Madam,<br />We thank you for your enquiry and are pleased to quote as follows:</p>

            {/* Items */}
            <table className="mt-3 w-full border-collapse" style={{ fontSize: base * 0.88 }}>
              <thead>
                <tr style={{ background: tpl.accent, color: "#fff" }}>
                  {["#", "Description", "Qty", "Unit", "Rate", "Disc %", "Tax %", "Amount"].map((h, i) => (
                    <th key={h} className="border px-2 py-1.5" style={{ borderColor: "#d1d5db", textAlign: i === 1 ? "left" : i === 7 || i === 4 ? "right" : "center" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(quote.items || []).map((it, i) => {
                  const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                  const amt = (line - line * (Number(it.discount_percent) || 0) / 100) * (1 + (Number(it.tax_percent) || 0) / 100);
                  return (
                    <tr key={i} style={{ background: i % 2 === 1 ? tpl.light : "#fff" }}>
                      <td className="border px-2 py-1.5 text-center" style={{ borderColor: "#d1d5db" }}>{i + 1}</td>
                      <td className="border px-2 py-1.5" style={{ borderColor: "#d1d5db" }}>{it.description}</td>
                      <td className="border px-2 py-1.5 text-center" style={{ borderColor: "#d1d5db" }}>{Number(it.quantity)}</td>
                      <td className="border px-2 py-1.5 text-center" style={{ borderColor: "#d1d5db" }}>{it.unit || "Nos"}</td>
                      <td className="border px-2 py-1.5 text-right" style={{ borderColor: "#d1d5db" }}>{Number(it.unit_price).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="border px-2 py-1.5 text-center" style={{ borderColor: "#d1d5db" }}>{Number(it.discount_percent) ? `${Number(it.discount_percent)}` : "—"}</td>
                      <td className="border px-2 py-1.5 text-center" style={{ borderColor: "#d1d5db" }}>{Number(it.tax_percent)}</td>
                      <td className="border px-2 py-1.5 text-right font-semibold" style={{ borderColor: "#d1d5db" }}>{amt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Totals */}
            <div className="mt-3 flex justify-end">
              <table style={{ fontSize: base * 0.9 }}>
                <tbody>
                  <tr><td className="px-3 py-0.5 text-right" style={{ color: "#6b7280" }}>Subtotal</td><td className="px-3 py-0.5 text-right font-semibold">{money(totals.sub)}</td></tr>
                  {totals.disc > 0 && <tr><td className="px-3 py-0.5 text-right" style={{ color: "#6b7280" }}>Discount</td><td className="px-3 py-0.5 text-right font-semibold">- {money(totals.disc)}</td></tr>}
                  <tr><td className="px-3 py-0.5 text-right" style={{ color: "#6b7280" }}>Tax (GST)</td><td className="px-3 py-0.5 text-right font-semibold">{money(totals.tax)}</td></tr>
                  <tr style={{ background: tpl.accent, color: "#fff" }}>
                    <td className="px-3 py-1.5 text-right font-bold">Grand Total</td>
                    <td data-testid="preview-grand-total" className="px-3 py-1.5 text-right font-bold">{money(grand)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {quote.notes && <p className="mt-4" style={{ fontSize: base * 0.9 }}><b>Notes:</b> {quote.notes}</p>}
            <p className="mt-4 font-bold" style={{ color: tpl.accent, fontSize: base * 0.98 }}>Terms &amp; Conditions</p>
            {terms.split("\n").filter((l) => l.trim()).map((l, i) => (
              <p key={i} style={{ color: "#6b7280", fontSize: base * 0.82 }}>{l.trim()}</p>
            ))}

            <div className="mt-10 text-right">
              <p className="font-bold" style={{ fontSize: base * 0.95 }}>For {activeLh?.company_name || "the Company"}</p>
              <p className="mt-10" style={{ color: "#6b7280", fontSize: base * 0.85 }}>Authorised Signatory</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
